<template lang="pug">
  .combo-box(v-click-outside="close")
    .combo-input(
      :id="keyId"
      role="combobox"
      :aria-labelledby="labelId"
      @click="toggleOpen"
      @keydown="handleKeyPress"
      :aria-expanded="comboBoxOpen.toString()"
      :aria-controls="menuId"
      :aria-activedescendant="activeDescendant"
      :aria-disabled="comboboxDisabled.toString()"
      tabindex="0"
      :class="[brandColor1 ? 'brand-color-1' : 'cta-color-1', { open: comboBoxOpen, disabled: comboboxDisabled }]"
    )
      span.combo-box-label.semi-bold(:id="labelId" :class="brandColor1 ? 'brand-color-1' : 'brand-color-8'") {{ label }}
      MrIcon.combo-box-arrow.xs-f-xsmall.max-at-tweak(name="caret-rounded" :fill="false" aria-hidden="true")
    .combo-menu(:class="{ open: comboBoxOpen }" tabindex="-1")
      ul.combo-menu-inner(
        :id="menuId"
        ref="combomenu"
        role="listbox"
        aria-multiselectable="true"
        :aria-labelledby="labelId"
        :inert="!comboBoxOpen"
      )
        li.combo-option.semi-bold(
          v-for="(o, index) in options"
          :key="o.value"
          :id="getOptionId(index)"
          :ref="'option' + index"
          @click="!o.disabled && selectOption(o)"
          role="option"
          :aria-selected="modelValue && modelValue.includes(o.value)"
          :aria-disabled="o.disabled ? o.disabled.toString() : undefined"
          :class="[brandColor1 ? 'brand-color-1' : 'brand-color-8', { selected: modelValue && modelValue.includes(o.value), focus: selectedIndex === index, disabled: o.disabled }]"
          tabindex="-1"
        )
          .filter-checkbox(:aria-hidden="true" :class="{'filter-checkbox--brand-color-1': brandColor1}")
            MrIcon.xs-f-xsmall.max-at-tweak(name="check-icon" style="width:1em;")
          span {{o.label}}
    .sr-only(aria-live="polite" aria-atomic="true") {{ screenReaderMessage }}
</template>

<script>
  import MrIcon from '@components/MrIcon';

  export default {
    name: "ComboBox",

    components: {
      MrIcon
    },

    props: {
      modelValue: {
        type: Array,
        default: () => [],
        required: true,
      },
      comboboxDisabled: {
        type: Boolean,
        default: false
      },
      label: {
        type: String,
        required: true,
      },
      keyId: {
        type: String,
        required: true
      },
      options: {
        type: Array,
        required: true
      },
      brandColor1: {
        type: Boolean,
        default: false,
      }
    },

    emits: ['update:modelValue'],

    data() {
      return {
        selectedIndex: -1,
        comboBoxOpen: false,
        screenReaderMessage: '',
      };
    },

    computed: {
      menuId() {
        return `${this.keyId}-menu`;
      },

      labelId() {
        return `${this.keyId}-label`;
      },

      activeDescendant() {
        if (this.comboBoxOpen && this.selectedIndex >= 0 && this.options[this.selectedIndex]) {
          return this.getOptionId(this.selectedIndex);
        }
        return undefined;
      }
    },

    watch: {
      options() {
        this.selectedIndex = this.options.findIndex(opt => !opt.disabled);
        if (this.selectedIndex === -1 && this.options.length > 0) {this.selectedIndex = 0;}
        else if (this.options.length === 0) {this.selectedIndex = -1;}
      }
    },

    methods: {
      getOptionId(index) {
        return `${this.keyId}-option-${index}`;
      },

      toggleOpen() {
        if (this.comboboxDisabled) {return;}
        this.comboBoxOpen = !this.comboBoxOpen;
        if (this.comboBoxOpen) {
          const firstSelected = this.modelValue?.length > 0 ?
            this.options.findIndex(opt => this.modelValue.includes(opt.value)) : -1;
          this.selectedIndex = firstSelected !== -1 ? firstSelected : (this.options.length > 0 ? 0 : -1);
          this.$nextTick(() => {
            this.scrollToSelected();
          });
        }
      },

      handleKeyPress(event) {
        if (this.comboboxDisabled) {return;}

        const { key } = event;

        if (key === 'Enter') {
          event.preventDefault();
          if (!this.comboBoxOpen) {
            this.toggleOpen();
          } else {
            if (this.selectedIndex >= 0 && this.options[this.selectedIndex] && !this.options[this.selectedIndex].disabled) {
              this.selectOption(this.options[this.selectedIndex]);
            }
          }
        } else if (key === 'Escape') {
          event.preventDefault();
          if (this.comboBoxOpen) {
            this.close();
          }
        } else if (key === 'ArrowDown') {
          event.preventDefault();
          if (!this.comboBoxOpen) {
            this.toggleOpen();
          } else {
            this.selectNext();
          }
        } else if (key === 'ArrowUp') {
          event.preventDefault();
          if (!this.comboBoxOpen) {
            this.toggleOpen();
          } else {
            if (event.altKey) {
              this.close();
            } else {
              this.selectPrevious();
            }
          }
        } else if (key === 'Tab') {
          if (this.comboBoxOpen) {
            this.close();
          }
        }
      },

      selectNext() {
        if (!this.options.length) {return;}
        let nextIndex = this.selectedIndex + 1;
        while (nextIndex < this.options.length && this.options[nextIndex].disabled) {
          nextIndex++;
        }
        if (nextIndex < this.options.length) {
          this.selectedIndex = nextIndex;
          this.scrollToSelected();
        }
      },

      selectPrevious() {
        if (!this.options.length) {return;}
        let prevIndex = this.selectedIndex - 1;
        while (prevIndex >= 0 && this.options[prevIndex].disabled) {
          prevIndex--;
        }
        if (prevIndex >= 0) {
          this.selectedIndex = prevIndex;
          this.scrollToSelected();
        }
      },

      scrollToSelected() {
        if (this.selectedIndex === -1 || !this.$refs.combomenu || !this.$refs['option' + this.selectedIndex] || !this.$refs['option' + this.selectedIndex][0]) {return;}

        const parentElement = this.$refs.combomenu;
        const activeElement = this.$refs['option' + this.selectedIndex][0];
        const { offsetHeight, offsetTop } = activeElement;
        const { offsetHeight: parentOffsetHeight, scrollTop } = parentElement;

        const isAbove = offsetTop < scrollTop;
        const isBelow = offsetTop + offsetHeight > scrollTop + parentOffsetHeight;

        if (isAbove) {
          parentElement.scrollTop = offsetTop - 14;
        } else if (isBelow) {
          parentElement.scrollTop = offsetTop - parentOffsetHeight + offsetHeight + 14;
        }
      },

      selectOption(option) {
        if (option.disabled) {return;}

        let newValue = this.modelValue ? [...this.modelValue] : [];
        const valueIndex = newValue.indexOf(option.value);

        if (valueIndex === -1) {
          newValue.push(option.value);
          this.screenReaderMessage = `${option.label} selected.`;
          this.trackMREvent(`Filter Selected - ${this.label}: ${option.label}`);
        } else {
          newValue.splice(valueIndex, 1);
          this.screenReaderMessage = `${option.label} unselected.`;
          this.trackMREvent(`Filter Unselected - ${this.label}: ${option.label}`);
        }
        this.$emit('update:modelValue', newValue);
      },

      close() {
        if (this.comboBoxOpen) {
          this.comboBoxOpen = false;
        }
      },
    }
  };
</script>

<style scoped lang="stylus">
.combo-box
  position relative
  cursor pointer
  display inline-block
  vertical-align top

  .combo-input
    padding 0.5em 1em
    padding-right 2em
    background-color color-white
    border-radius 1.5em
    box-shadow 0px 2px 4px 0px rgba(0, 0, 0, 0.05)
    width 11em
    overflow hidden
    text-overflow ellipsis
    white-space nowrap

    .combo-box-arrow
      position absolute
      right 1em
      top 50%
      margin-top -0.25em
      transform rotate(180deg)
      transition transform 350ms ease

    &.open
      .combo-box-arrow
        transform rotate(0deg)

    &.disabled
      opacity 0.3
      pointer-events none

    &:focus
      outline 2px solid cta-color-1
      outline-offset 2px
      &.brand-color-1
        outline 2px solid brand-color-1

  .combo-menu
    position absolute
    top 2.75em
    left -2em
    max-height 0
    transition(max-height 300ms ease-in-out)
    overflow hidden
    z-index 2
    min-width 16em

    &.open
      max-height 20em

    .combo-menu-inner
      background-color color-white
      border-radius 20px
      box-shadow 0px 2px 4px 0px rgba(0, 0, 0, 0.05)
      max-height 17em
      overflow auto
      padding 1em

    .combo-option
      position relative
      padding 0.5em 1em
      padding-left 2.5em
      border-radius 1.5em

      &.focus
      &:hover
        background-color ui-color-3

      .filter-checkbox
        position absolute
        top 0.5em
        left 0.5em
        border 2px solid brand-color-8
        height 1.5em
        width 1.5em
        border-radius 9px
        display inline-block
        &--brand-color-1
          border-color brand-color-1
        .mr-icon
          position absolute
          top 50%
          left 50%
          margin-top -0.35em
          margin-left -0.5em
          color transparent
      &.focus
        .filter-checkbox--brand-color-1
          border-color brand-color-1
      &:hover
        .filter-checkbox--brand-color-1
          border-color brand-color-1
      &.selected
        .filter-checkbox
          border-color cta-color-4
          background-color cta-color-4
          &--brand-color-1
            border-color brand-color-1
            background-color brand-color-1
          .mr-icon
            color color-white

      &.disabled
        opacity 0.3
        pointer-events none

.sr-only
  position absolute
  width 1px
  height 1px
  padding 0
  margin -1px
  overflow hidden
  clip rect(0, 0, 0, 0)
  white-space nowrap
  border-width 0
</style>
